/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-len */
import { useState } from 'react'
import { message } from 'antd'
import { useAppDispatch, useAppSelector } from 'store'
import { AiAgentMessage } from '../ai-agent.type'
import {
  createContextMessages,
  extractPolicyCodes,
  extractInsuranceClass,
  isUserAdmin,
  ContextType,
  ContextConfig,
  FeatureToggleType,
  FeatureToggleConfig,
} from './ai-agent.context'
import { extractPageNumber } from '../libs/policy-reference.utils'
import { openPolicyPdfWithContext } from '../libs/policy-navigation'
import { loadComparisonByPolicyCodesHelper } from '../libs/comparison-loader'
import { updateSelectionModal, removeAllSelections, updateFilter } from 'slices/comparison'
import { CompareBy, ComparisonColumn } from 'types/enums'
import { extractFeatureIdsFromText, buildSectionFilterMap } from '../libs/feature-filters'

const MEDIA_URL: string = process.env.REACT_APP_MEDIA_BASE_URL || ''

interface UseChatContextReturn {
  // Context state
  includeHistory: boolean
  contextConfigs: ContextConfig[]
  featureToggles: FeatureToggleConfig[]

  // Context availability checks
  hasClassAndCoverSelections: boolean
  hasPoliciesSelected: boolean
  hasCompleteContext: boolean
  isAdmin: boolean

  // Context methods
  updateContextConfig: (type: ContextType, enabled: boolean) => void
  getContextConfig: (type: ContextType) => boolean
  updateFeatureToggle: (type: FeatureToggleType, enabled: boolean) => void
  getFeatureToggle: (type: FeatureToggleType) => boolean
  setIncludeHistory: (enabled: boolean) => void
  prepareHistoryWithContext: (messageHistory: AiAgentMessage[]) => AiAgentMessage[]
  getPolicyCodes: () => string[]
  getInsuranceClass: () => string
  getCoverIds: () => string[] | number[]
  openSelectionModal: () => void
  getInitialMessages: () => AiAgentMessage[]
  openPolicyPdf: (pdfPath?: string, reference?: string | number, page?: string | number) => void
  getSelectedPolicyContents: () => any[]
  getAllPolicies: () => any[]
  loadComparisonByPolicyCodes: (codes: string[]) => Promise<void>
  getFeatureIdsFromText: (text: string) => number[]
  applyFeatureFiltersByIds: (featureIds: number[]) => void
  getUserId: () => string | undefined
  getPolicyIds: () => string[] | number[]
  getCountryCode: () => string
  // Context toggles for UI (computed from contextConfigs)
  contextToggles: Array<{
    type: ContextType | 'history'
    enabled: boolean
    label: string
    onChange: (enabled: boolean) => void
  }>
}

const INITIAL_BOT_MESSAGE: AiAgentMessage = {
  id: '1',
  sender: 'assistant',
  text: `My name is AL, your personal Ai chat assistant. How can I help you today?`,
  timestamp: Date.now(),
}

const getPolicyRequiredMessage = (compareBy: CompareBy): AiAgentMessage => {
  const isCompanyMode = compareBy === CompareBy.COMPANY
  const selectionText = isCompanyMode ? 'policies' : 'features'
  const actionText = isCompanyMode ? 'Select policies to compare' : 'Select features to compare'

  const messageText =
    `<strong>Select ${selectionText} to start a chat</strong><br>` +
    `<ul>` +
    `<li>Choose a product class and covers</li>` +
    `<li>${actionText}</li>` +
    `</ul>` +
    `I'll be ready to help once you've made your selections.`

  return {
    id: 'steps-required',
    sender: 'assistant',
    text: messageText,
    timestamp: Date.now(),
  }
}

// This is for integration chatbot with the comparison app
// When using chatbot in different apps, update this file to match the context of the app (RiskCoach, BiCalculator, etc)
function useChatContext(): UseChatContextReturn {
  const dispatch = useAppDispatch()
  // const navigate = useNavigate()

  const [includeHistory, setIncludeHistory] = useState(true)
  const [contextConfigs, setContextConfigs] = useState<ContextConfig[]>([
    {
      type: 'user' as const,
      enabled: false,
      label: 'User Info',
      prefix: 'User information',
    },
    {
      type: 'selections' as const,
      enabled: false,
      label: 'Selections',
      prefix: 'Current comparison selections',
    },
    {
      type: 'filters' as const,
      enabled: false,
      label: 'Filters',
      prefix: 'Current filter settings',
    },
  ])

  const [featureToggles, setFeatureToggles] = useState<FeatureToggleConfig[]>([])

  // Use specific selectors instead of entire state
  const selectedProductClassCover = useAppSelector(
    (state) => state.comparison.selectedProductClassCover,
  )
  const selectedPolicyContents = useAppSelector((state) => state.comparison.selectedPolicyContents)
  const selectedSectionCovers = useAppSelector((state) => state.comparison.selectedSectionCovers)
  const account = useAppSelector((state) => state.auth.account)
  const filter = useAppSelector((state) => state.comparison.filter)
  const comparisonPolicies = useAppSelector((state) => state.comparison.comparisonPolicies)
  const comparisonFeatures = useAppSelector((state) => state.comparison.comparisonFeatures)

  // Check if there are any selections to determine if context toggle should be shown
  const hasClassAndCoverSelections = useAppSelector((state) => {
    return (
      !!state.comparison.selectedProductClassCover?.className &&
      !!state.comparison.selectedProductClassCover?.coverId?.length
    )
  })

  // Check if policies are selected based on comparison mode
  const hasPoliciesSelected = useAppSelector((state) => {
    const { filter, selectedPolicyContents, selectedSectionCovers } = state.comparison

    if (filter.compareBy === CompareBy.COMPANY) {
      return !!selectedPolicyContents?.length
    } else if (filter.compareBy === CompareBy.FEATURE) {
      return !!selectedSectionCovers?.length
    }

    return false
  })

  // Check if user is admin
  const isAdmin = useAppSelector((state) => isUserAdmin(state))

  // Check if we have complete context (product class + policies/sections based on mode)
  const hasCompleteContext = useAppSelector((state) => {
    const { filter, selectedProductClassCover, selectedPolicyContents, selectedSectionCovers } =
      state.comparison

    if (!selectedProductClassCover?.className) {
      return false
    }

    if (filter.compareBy === CompareBy.COMPANY && !selectedPolicyContents?.length) {
      return true
    }
    if (filter.compareBy === CompareBy.FEATURE && !selectedSectionCovers?.length) {
      return true
    }

    if (filter.compareBy === CompareBy.COMPANY) {
      return !!selectedPolicyContents?.length
    } else if (filter.compareBy === CompareBy.FEATURE) {
      return !!selectedSectionCovers?.length
    }

    return false
  })

  const openSelectionModal = () => {
    dispatch(updateSelectionModal({ open: true }))
  }

  // Context management methods
  const updateContextConfig = (type: ContextType, enabled: boolean) => {
    setContextConfigs((prev) =>
      prev.map((config) => (config.type === type ? { ...config, enabled } : config)),
    )
  }

  const getContextConfig = (type: ContextType) => {
    return contextConfigs.find((config) => config.type === type)?.enabled ?? false
  }

  // Feature toggle management methods
  const updateFeatureToggle = (type: FeatureToggleType, enabled: boolean) => {
    setFeatureToggles((prev) =>
      prev.map((toggle) => (toggle.type === type ? { ...toggle, enabled } : toggle)),
    )
  }

  const getFeatureToggle = (type: FeatureToggleType) => {
    const toggle = featureToggles.find((t) => t.type === type)
    if (!toggle) return false
    // If admin-only, check if user is admin
    if (toggle.adminOnly && !isAdmin) return false
    return toggle.enabled
  }

  const prepareHistoryWithContext = (messageHistory: AiAgentMessage[]) => {
    let messagesToSend = messageHistory
    const contextMessages: AiAgentMessage[] = []

    // Create a minimal state object for context creation
    const minimalState = {
      comparison: {
        selectedProductClassCover,
        selectedPolicyContents,
        selectedSectionCovers,
        filter,
      },
    } as any
    // Get context messages based on configuration
    const contextMsgs = createContextMessages(minimalState, contextConfigs)
    contextMessages.push(...contextMsgs)

    // Add context messages to the beginning
    if (contextMessages.length > 0) {
      messagesToSend = [...contextMessages, ...messagesToSend]
    }

    // Include conversation history if enabled
    if (includeHistory) {
      return messagesToSend
    } else {
      // Only send the context messages and current message, no history
      const lastMessage = messagesToSend[messagesToSend.length - 1]
      return contextMessages.length > 0 ? [...contextMessages] : [lastMessage]
    }
  }

  const getPolicyCodes = () => {
    // Create minimal state for policy code extraction
    const minimalState = {
      comparison: {
        selectedPolicyContents,
      },
    } as any
    return extractPolicyCodes(minimalState)
  }

  const getInsuranceClass = () => {
    // Create minimal state for insurance class extraction
    const minimalState = {
      comparison: {
        selectedProductClassCover,
      },
    } as any // Type assertion for compatibility
    return extractInsuranceClass(minimalState)
  }

  const getCoverIds = (): string[] | number[] => {
    if (!selectedProductClassCover?.coverId?.length) return []
    return selectedProductClassCover.coverId
  }

  const getSelectedPolicyContents = () => {
    return selectedPolicyContents
  }

  const getAllPolicies = () => comparisonPolicies || []

  const getUserId = () => {
    if (!account?.id) return undefined
    return String(account.id)
  }

  const getPolicyIds = (): string[] | number[] => {
    if (!selectedPolicyContents?.length) return []
    // Prefer policy ids from the selected policy objects
    const idsFromPolicy = selectedPolicyContents.map((pc: any) => `${pc?.id}`)

    return Array.from(new Set(idsFromPolicy))
  }

  const getCountryCode = () => {
    const code = account?.buy_group?.country_code || process.env.REACT_APP_COUNTRY_CODE || 'AU'
    return code.toUpperCase()
  }

  const openPolicyPdf = (
    pdfPath?: string,
    policyCode?: string | number,
    page?: string | number,
  ): void => {
    const pageNumber = page ? extractPageNumber(page.toString()) : null
    openPolicyPdfWithContext({
      pdfPath,
      policyCode,
      page: pageNumber ?? undefined,
      comparisonPolicies: comparisonPolicies || undefined,
      selectedPolicyContents,
      mediaBaseUrl: MEDIA_URL,
    })
  }

  const loadComparisonByPolicyCodes = async (codes: string[]) => {
    const uniqueCodes = Array.from(new Set(codes.map((c) => c?.trim()).filter(Boolean)))
    if (uniqueCodes.length === 0) return

    if (!selectedProductClassCover?.coverId?.length) {
      openSelectionModal()
      message.error('Please choose a class and covers first')
      return
    }

    const maxPolicies = ComparisonColumn.MAX_SELECTED_LENGTH
    const codesToLoad =
      uniqueCodes.length > maxPolicies ? uniqueCodes.slice(0, maxPolicies) : uniqueCodes

    if (uniqueCodes.length > maxPolicies) {
      message.warning(
        `You can only load up to ${maxPolicies} policies. Loading the first ${maxPolicies}.`,
      )
    }

    await dispatch(removeAllSelections())

    const result = await loadComparisonByPolicyCodesHelper({
      codes: codesToLoad,
      selectedProductClassCover,
      filter,
      comparisonPolicies,
      dispatch,
    })

    if (result === 'missing-covers') {
      message.error('Please choose a class and covers first')
      return
    }
    if (result === 'no-match') {
      message.error('No matching policies found in current covers')
      return
    }
    message.success('Comparison loaded')
  }

  const getInitialMessages = () => {
    const compareBy = filter.compareBy
    return hasCompleteContext ? [INITIAL_BOT_MESSAGE] : [getPolicyRequiredMessage(compareBy)]
  }

  const getFeatureIdsFromText = (text: string): number[] =>
    extractFeatureIdsFromText({ text, comparisonFeatures })

  const applyFeatureFiltersByIds = (featureIds: number[]) => {
    message.error(
      'Feature filtering is currently under development. Please use the manual filter controls for now.',
    )
    if (featureIds?.length) return
    const sectionToIds = buildSectionFilterMap({ featureIds, comparisonFeatures })
    dispatch(updateFilter({ ...filter, filterFeature: sectionToIds }))
    message.success('Feature filters applied')
  }

  const contextToggles = [
    ...contextConfigs.map((config) => ({
      type: config.type,
      enabled: config.enabled,
      label: config.label,
      onChange: (enabled: boolean) => updateContextConfig(config.type, enabled),
    })),
    {
      type: 'history' as const,
      enabled: includeHistory,
      label: 'Chat History',
      onChange: setIncludeHistory,
    },
  ]
  return {
    includeHistory,
    contextConfigs,
    featureToggles,
    hasClassAndCoverSelections,
    hasPoliciesSelected,
    hasCompleteContext,
    isAdmin,
    updateContextConfig,
    getContextConfig,
    updateFeatureToggle,
    getFeatureToggle,
    setIncludeHistory,
    prepareHistoryWithContext,
    getPolicyCodes,
    getInsuranceClass,
    getCoverIds,
    openSelectionModal,
    getInitialMessages,
    openPolicyPdf,
    getSelectedPolicyContents,
    getAllPolicies,
    getUserId,
    getPolicyIds,
    getCountryCode,
    loadComparisonByPolicyCodes,
    getFeatureIdsFromText,
    applyFeatureFiltersByIds,
    contextToggles,
  }
}

export { useChatContext }
export type { ContextConfig }
